<template>
    <v-card
        elevation="3"
        outlined
        class="card-servidor"
        :class="`situacao-${servidor.situacao}`"
        @click="$emit('abrir', servidor)"
    >
        <div class="d-flex align-center mb-2">
            <v-avatar tile rounded size="34" :color="cor" class="br-5 mr-2">
                <v-icon small color="white">{{ icone }}</v-icon>
            </v-avatar>

            <div class="flex-grow-1 minimo">
                <div class="nome">{{ servidor.nome }}</div>
                <div class="host text--secondary">{{ servidor.host }}</div>
            </div>

            <v-btn v-if="podeEditar" icon small @click.stop="$emit('configurar', servidor)">
                <v-icon small>mdi-cog-outline</v-icon>
            </v-btn>
        </div>

        <div class="d-flex align-center mb-2">
            <v-chip x-small label dark :color="cor" class="mr-2">
                {{ rotuloSituacao }}
            </v-chip>

            <span class="text-caption" :class="atrasado ? 'red--text font-weight-medium' : 'text--secondary'">
                {{ textoColeta }}
            </span>
        </div>

        <div v-if="servidor.situacao === 'aguardando'" class="aguardando text--secondary">
            <v-icon x-small color="grey">mdi-timer-sand</v-icon>
            Nenhuma métrica recebida ainda.
            <span v-if="!servidor.coleta_local && !servidor.tem_token">
                Gere o token e instale o agente.
            </span>
        </div>

        <template v-else>
            <div v-for="barra in barras" :key="barra.coluna" class="linha-barra">
                <span class="rotulo-barra">{{ barra.label }}</span>
                <v-progress-linear
                    class="mx-2 flex-grow-1"
                    height="6"
                    rounded
                    :value="barra.percentual"
                    :color="corDaFaixa(barra.faixa)"
                />
                <span class="valor-barra" :class="classeDaFaixa(barra.faixa)">{{ barra.valor }}</span>
            </div>

            <div class="rodape text--secondary">
                <span v-if="servidor.cpu_cores">{{ servidor.cpu_cores }} núcleos</span>
                <span v-if="servidor.cpu_cores && servidor.uptime_seconds"> · </span>
                <span v-if="servidor.uptime_seconds">{{ uptime }} no ar</span>
            </div>

            <div v-if="servicosFora.length" class="fora red--text">
                <v-icon x-small color="red">mdi-alert-circle</v-icon>
                fora do ar: {{ servicosFora.join(', ') }}
            </div>

            <div v-if="alertas" class="fora" :class="servidor.situacao === 'critico' ? 'red--text' : 'orange--text text--darken-2'">
                <v-icon x-small :color="servidor.situacao === 'critico' ? 'red' : 'orange darken-2'">mdi-bell-ring</v-icon>
                {{ alertas }} {{ alertas === 1 ? 'alerta aberto' : 'alertas abertos' }}
            </div>
        </template>
    </v-card>
</template>

<script>
import { formatarValor } from './catalogo';
import { corDaSituacao, iconeDoPapel, textoDaSituacao } from './servidores';

export default {
    name: 'CardServidor',

    props: {
        servidor: { type: Object, required: true },
        podeEditar: { type: Boolean, default: false },
    },

    computed: {
        cor() {
            return corDaSituacao(this.servidor.situacao);
        },

        icone() {
            return iconeDoPapel(this.servidor.papel);
        },

        rotuloSituacao() {
            return textoDaSituacao(this.servidor.situacao);
        },

        alertas() {
            return this.servidor.alertas_abertos || 0;
        },

        servicosFora() {
            return this.servidor.services_down || [];
        },

        atrasado() {
            return this.servidor.situacao === 'sem_coleta';
        },

        textoColeta() {
            if (this.servidor.coletado_em === null) return 'sem histórico';

            const minutos = this.servidor.minutos_sem_coleta;

            if (minutos <= 1) return 'há 1 min';
            if (minutos < 60) return `há ${minutos} min`;
            if (minutos < 1440) return `há ${Math.floor(minutos / 60)}h`;

            return `há ${Math.floor(minutos / 1440)}d`;
        },

        uptime() {
            const s = this.servidor.uptime_seconds || 0;
            const dias = Math.floor(s / 86400);
            const horas = Math.floor((s % 86400) / 3600);
            return dias ? `${dias}d` : `${horas}h`;
        },

        barras() {
            const m = this.servidor.metricas || {};
            const f = this.servidor.faixas || {};

            return [
                {
                    coluna: 'cpu_percent',
                    label: 'CPU',
                    valor: formatarValor('cpu_percent', m.cpu_percent),
                    percentual: this.percentual(m.cpu_percent),
                    faixa: f.cpu_percent,
                },
                {
                    coluna: 'mem_used_percent',
                    label: 'Memória',
                    valor: formatarValor('mem_used_percent', m.mem_used_percent),
                    percentual: this.percentual(m.mem_used_percent),
                    faixa: f.mem_used_percent,
                },
                {
                    coluna: 'disk_used_percent',
                    label: 'Disco',
                    valor: formatarValor('disk_used_percent', m.disk_used_percent),
                    percentual: this.percentual(m.disk_used_percent),
                    faixa: f.disk_used_percent,
                },
            ];
        },
    },

    methods: {
        percentual(valor) {
            return valor === null || valor === undefined ? 0 : Math.min(100, valor);
        },

        corDaFaixa(faixa) {
            if (faixa === 'critico') return 'red darken-1';
            if (faixa === 'aviso') return 'amber darken-2';
            return 'blue-grey darken-1';
        },

        classeDaFaixa(faixa) {
            if (faixa === 'critico') return 'red--text font-weight-medium';
            if (faixa === 'aviso') return 'orange--text text--darken-2 font-weight-medium';
            return '';
        },
    },
};
</script>

<style scoped>
.card-servidor {
    padding: 12px;
    height: 100%;
    cursor: pointer;
    transition: box-shadow 0.15s ease;
}

.card-servidor:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18) !important;
}

.card-servidor.situacao-aviso {
    border-color: #ff8f00 !important;
}

.card-servidor.situacao-critico,
.card-servidor.situacao-sem_coleta {
    border-color: #c62828 !important;
}

.minimo {
    min-width: 0;
}

.nome {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.host {
    font-size: 11px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.linha-barra {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
}

.rotulo-barra {
    font-size: 11px;
    width: 54px;
    opacity: 0.75;
}

.valor-barra {
    font-size: 12px;
    width: 46px;
    text-align: right;
}

.rodape,
.fora,
.aguardando {
    font-size: 11px;
    margin-top: 6px;
}
</style>
